import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOptionalSession } from '@hooks/security/use-session';
import type { ReactNode } from 'react';

export type TDashWorkflow = 'nkr' | 'pl';

export interface IShell {
    registrationId: number;
    editKey: string;
    firstName: string;
    lastName: string;
    showMentorButton: boolean;
    hasMultipleLanguages: boolean;
    connectToDonorStatus: number | null;
    languageData: Record<string, string>;
    supportPhone: string | null;
    workflow: TDashWorkflow;
    subdomain: string;
    centerName: string;
    centerSiteUrl: string | null;
    centerLogoUrl: string | null;
    centerBackground: string | null;
}

export const defaultShell: IShell = {
    registrationId: 0,
    editKey: '',
    firstName: '',
    lastName: '',
    showMentorButton: false,
    hasMultipleLanguages: false,
    connectToDonorStatus: null,
    languageData: {},
    supportPhone: null,
    workflow: 'nkr',
    subdomain: '',
    centerName: '',
    centerSiteUrl: null,
    centerLogoUrl: null,
    centerBackground: null
};

interface IShellResponse {
    success: boolean;
    results: Partial<IShell>;
}

const SHELL_ENDPOINT = '/api/register/shell';

const ShellValueContext = createContext<IShell>(defaultShell);
const ShellRefreshContext = createContext<() => void>(() => {});

interface ILoadedShell {
    requestKey: string;
    shell: IShell;
}

export const ShellProvider = ({ children }: { children: ReactNode }) => {
    const session = useOptionalSession();
    const [loaded, setLoaded] = useState<ILoadedShell>({ requestKey: '', shell: defaultShell });
    const [refreshCount, setRefreshCount] = useState(0);

    const path = session ? `/${session.registrationId}/${session.editKey}` : '';
    const requestKey = path + '#' + refreshCount;

    const shell = useMemo(() => {
        if (path || loaded.requestKey === requestKey) return loaded.shell;

        return {
            ...loaded.shell,
            registrationId: 0,
            editKey: '',
            showMentorButton: false,
            connectToDonorStatus: null,
        };
    }, [loaded, path, requestKey]);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        axios
            .get<IShellResponse>(SHELL_ENDPOINT + path, {
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            })
            .then((response) => {
                if (!active) return;

                setLoaded({ requestKey, shell: { ...defaultShell, ...response.data.results } });
            })
            .catch(() => {
                // Chrome only: a failure leaves the defaults in place rather than
                // blocking the page.
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [path, requestKey]);

    const refresh = useCallback(() => setRefreshCount((count) => count + 1), []);

    return (
        <ShellRefreshContext.Provider value={refresh}>
            <ShellValueContext.Provider value={shell}>{children}</ShellValueContext.Provider>
        </ShellRefreshContext.Provider>
    );
};

export const useShell = (): IShell => useContext(ShellValueContext);

export const useRefreshShell = (): (() => void) => useContext(ShellRefreshContext);
